import { IconButton, Stack } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { ModeEdit } from '@mui/icons-material';
import { ColDef, GetRowIdParams, ICellRendererParams, ValueFormatterFunc } from 'ag-grid-community';
import { useCallback, useMemo } from 'react';
import { FieldValues } from 'react-hook-form';
import { AgTableWithBaseStyle } from '../../../components/AgTable/AgTable2';
import { AgFormatterService } from '../../../util/ag-formatter-service';
import { agDateColumnFilterComparator } from '../../../util/ag-grid-utils';
import { FormatterService } from '../../../util/formatter-service';
import { IForm, IFormField } from '../../../view-models/form.view-model';
import { DataWithId } from './UseConfigActions';

export interface IConfigPageContentsGridProps<T extends FieldValues & DataWithId> {
  forms: IForm<T>[];
  onDelete?: (item: T) => Promise<void> | void;
  onEdit?: (item: T) => Promise<void> | void;
}

const ACTION_COL_ID = '__configActions';
const ACTION_COL_WIDTH = 100;

export function ConfigPageContentsGrid<T extends FieldValues & DataWithId>(
  props: IConfigPageContentsGridProps<T>
) {
  const { forms, onDelete, onEdit } = props;

  const fields = useMemo<IFormField<unknown>[]>(
    () => (forms[0] ? [...forms[0].fields.values()] : []),
    [forms]
  );

  // Disabled flag lives on the form, not on the row data — track separately by id so the
  // row data passed to onEdit/onDelete stays clean (no synthetic fields leaking out).
  const disabledById = useMemo(() => {
    const map = new Map<number | string, boolean>();
    forms.forEach((form) => {
      if (form.data?.id != null) {
        map.set(form.data.id, Boolean(form.disabled));
      }
    });
    return map;
  }, [forms]);

  // Field columns depend only on the schema (`fields`), so they don't rebuild when the row
  // set changes. The action column has its own memo and uses `cellRendererParams` for the
  // dynamic closures so cells re-render in place rather than remounting on data refresh.
  const fieldCols = useMemo<ColDef[]>(
    () =>
      fields.map((field) => {
        const valueFormatter = AgFormatterService.get().getFormatterForFormField<T, unknown>(field);
        return {
          field: field.key,
          headerName: field.label,
          sortable: true,
          valueFormatter,
          ...filterDefsForField(field, valueFormatter),
        };
      }),
    [fields]
  );

  const actionsCol = useMemo<ColDef>(
    () => ({
      colId: ACTION_COL_ID,
      headerName: '',
      pinned: 'right',
      // flex: 0 keeps this column out of the flex pool so field columns get all the
      // available width; the fixed width below stays the same regardless of viewport size.
      flex: 0,
      width: ACTION_COL_WIDTH,
      minWidth: ACTION_COL_WIDTH,
      maxWidth: ACTION_COL_WIDTH,
      suppressSizeToFit: true,
      sortable: false,
      filter: false,
      resizable: false,
      cellRenderer: ActionCellRenderer,
      // Cast the handlers' parameter type from T to DataWithId — the renderer only
      // forwards row data through, and at runtime each row IS a T (which extends DataWithId).
      cellRendererParams: {
        disabledById,
        onEdit: onEdit as ActionCellExtras['onEdit'],
        onDelete: onDelete as ActionCellExtras['onDelete'],
      } satisfies ActionCellExtras,
    }),
    [disabledById, onEdit, onDelete]
  );

  const columnDefs = useMemo<ColDef[]>(() => [...fieldCols, actionsCol], [fieldCols, actionsCol]);

  const rowData = useMemo(
    () => forms.map((form) => form.data).filter((data): data is T => data != null),
    [forms]
  );

  const getRowId = useCallback((params: GetRowIdParams<T>) => String(params.data.id), []);

  // domLayout='autoHeight' lets the grid size itself to fit its rows so we don't depend
  // on an ancestor having a constrained height — config-page route layout doesn't provide
  // one, which made the grid collapse to header-only.
  return (
    <AgTableWithBaseStyle
      columnDefs={columnDefs}
      defaultColDef={DEFAULT_COL_DEF}
      rowData={rowData}
      getRowId={getRowId}
      domLayout='autoHeight'
    />
  );
}

// flex: 1 makes field columns share available width equally and keeps that
// behavior on viewport resize — unlike fitGridWidth, which only applies once on init.
// minWidth is a readability floor: with many columns the flex share would otherwise
// shrink each column until the headers are unreadable. Once the combined minimums
// exceed the container the grid scrolls horizontally instead of squashing.
const DEFAULT_COL_DEF: ColDef = { flex: 1, minWidth: 150 };

interface ActionCellExtras {
  disabledById: Map<number | string, boolean>;
  onEdit?: (item: DataWithId) => Promise<void> | void;
  onDelete?: (item: DataWithId) => Promise<void> | void;
}

function ActionCellRenderer(params: ICellRendererParams<DataWithId> & ActionCellExtras) {
  const { data, disabledById, onEdit, onDelete } = params;
  if (!data) {
    return null;
  }
  const disabled = disabledById.get(data.id) ?? false;
  return (
    <Stack direction='row' columnGap={'0.5rem'} alignItems='center' height='100%'>
      {onEdit && (
        <IconButton
          aria-label='edit'
          color='secondary'
          size='small'
          onClick={() => onEdit(data)}
          disabled={disabled}
        >
          <ModeEdit />
        </IconButton>
      )}
      {onDelete && (
        <IconButton
          aria-label='delete'
          color='secondary'
          size='small'
          onClick={() => onDelete(data)}
          disabled={disabled}
        >
          <DeleteOutlineIcon />
        </IconButton>
      )}
    </Stack>
  );
}

function filterDefsForField(
  field: IFormField<unknown>,
  valueFormatter: ValueFormatterFunc
): Pick<ColDef, 'filter' | 'filterParams'> {
  switch (field.dataType) {
    case 'number':
      return { filter: 'agNumberColumnFilter' };
    case 'date':
      return {
        filter: 'agDateColumnFilter',
        filterParams: { comparator: agDateColumnFilterComparator, includeTime: false },
      };
    default: {
      // AG Grid's Set Filter shows raw cell values unless given a valueFormatter, and sorts by raw
      // key unless given a comparator — reuse the column's formatter so the list matches the cells.
      const labelFor = FormatterService.get().getFormatterForFormField<unknown>(field);
      return {
        filter: true,
        filterParams: {
          valueFormatter,
          comparator: (a: unknown, b: unknown) => String(labelFor(a)).localeCompare(String(labelFor(b))),
        },
      };
    }
  }
}
